import React, { useState, useRef, useEffect } from 'react';
import axios from '../../../utils/axios';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';
import toast from 'react-hot-toast';
import { usePreferences } from '../../../context/PreferencesContext';

const GeneralSettingsTab = () => {
  const [loading, setLoading] = useState(false);
  const { theme, setTheme } = usePreferences();
  const [settings, setSettings] = useState({
    organizationName: "TechCorp Industries",
    timezone: "Asia/Kolkata",
    dateFormat: "DD/MM/YYYY",
    currency: "INR",
    language: "en",
    sessionTimeout: "30",
    enableMFA: true,
    enableSSO: true,
    enableAuditLog: true,
    maintenanceMode: false,
    emailNotificationsEnabled: true,
    companyLogo: null,
    favicon: null
  });

  const [isModified, setIsModified] = useState(false);
  const [logoPreview, setLogoPreview] = useState(null);
  const [faviconPreview, setFaviconPreview] = useState(null);
  
  const logoInputRef = useRef(null);
  const faviconInputRef = useRef(null);

  const timezoneOptions = [
    { value: "Asia/Kolkata", label: "Indian Standard Time (IST)" },
    { value: "America/New_York", label: "Eastern Time (ET)" },
    { value: "America/Chicago", label: "Central Time (CT)" },
    { value: "America/Denver", label: "Mountain Time (MT)" },
    { value: "America/Los_Angeles", label: "Pacific Time (PT)" },
    { value: "UTC", label: "Coordinated Universal Time (UTC)" }
  ];

  const dateFormatOptions = [
    { value: "DD/MM/YYYY", label: "DD/MM/YYYY (Indian/European Format)" },
    { value: "MM/DD/YYYY", label: "MM/DD/YYYY (US Format)" },
    { value: "YYYY-MM-DD", label: "YYYY-MM-DD (ISO Format)" }
  ];

  const currencyOptions = [
    { value: "INR", label: "Indian Rupee (₹)" },
    { value: "USD", label: "US Dollar ($)" },
    { value: "EUR", label: "Euro (€)" },
    { value: "GBP", label: "British Pound (£)" },
    { value: "CAD", label: "Canadian Dollar (C$)" }
  ];

  const languageOptions = [
    { value: "en", label: "English" },
    { value: "hi", label: "Hindi" },
    { value: "ta", label: "Tamil" },
    { value: "te", label: "Telugu" },
    { value: "bn", label: "Bengali" },
    { value: "mr", label: "Marathi" },
    { value: "es", label: "Spanish" },
    { value: "fr", label: "French" },
    { value: "de", label: "German" }
  ];

  const sessionTimeoutOptions = [
    { value: "1", label: "1 minute (Test Mode)" },
    { value: "5", label: "5 minutes" },
    { value: "15", label: "15 minutes" },
    { value: "30", label: "30 minutes" },
    { value: "60", label: "1 hour" },
    { value: "120", label: "2 hours" },
    { value: "480", label: "8 hours" }
  ];

  const fetchSettings = async () => {
    try {
      const response = await axios.get('/general-settings');
      
      if (response.data.success && response.data.data) {
        const data = response.data.data;
        const currentTimeout = data.session_timeout ? String(data.session_timeout) : "30";
        
        setSettings({
          organizationName: data.organization_name || "",
          timezone: data.timezone || "Asia/Kolkata",
          dateFormat: data.date_format || "DD/MM/YYYY",
          currency: data.currency || "INR",
          language: data.language || "en",
          sessionTimeout: currentTimeout,
          enableMFA: Boolean(data.enable_mfa),
          enableSSO: Boolean(data.enable_sso),
          enableAuditLog: Boolean(data.enable_audit_log),
          maintenanceMode: Boolean(data.maintenance_mode),
          emailNotificationsEnabled: data.email_notifications_enabled !== undefined ? Boolean(data.email_notifications_enabled) : true,
          companyLogo: null,
          favicon: null
        });

        sessionStorage.setItem('session_timeout', currentTimeout);
        
        setLogoPreview(data.company_logo_url || null);
        setFaviconPreview(data.favicon_url || null);
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  const handleInputChange = (field, value) => {
    setSettings(prev => ({
      ...prev,
      [field]: value
    }));
    setIsModified(true);
  };

  const handleFileUpload = (field, file, type) => {
    if (file) {
      // Validate file type
      const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/svg+xml', 'image/webp'];
      if (!validTypes.includes(file.type)) {
        alert('Invalid file type. Please upload JPEG, PNG, SVG, or WEBP images.');
        return;
      }

      // Validate file size (max 2MB for favicon, 5MB for logo)
      const maxSize = type === 'favicon' ? 2 * 1024 * 1024 : 5 * 1024 * 1024;
      if (file.size > maxSize) {
        alert(`File size exceeds ${maxSize / (1024 * 1024)}MB limit.`);
        return;
      }

      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        if (type === 'logo') {
          setLogoPreview(reader.result);
        } else {
          setFaviconPreview(reader.result);
        }
      };
      reader.readAsDataURL(file);

      // Store file for upload
      handleInputChange(field, file);
    }
  };

  const handleRemoveImage = (type) => {
    if (type === 'logo') {
      setLogoPreview(null);
      handleInputChange('companyLogo', null);
      if (logoInputRef.current) logoInputRef.current.value = '';
    } else {
      setFaviconPreview(null);
      handleInputChange('favicon', null);
      if (faviconInputRef.current) faviconInputRef.current.value = '';
    }
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      
      const formData = new FormData();
      
      formData.append('organization_name', settings.organizationName || '');
      formData.append('timezone', settings.timezone || '');
      formData.append('date_format', settings.dateFormat || '');
      formData.append('currency', settings.currency || '');
      formData.append('language', settings.language || '');
      formData.append('session_timeout', settings.sessionTimeout || '');
      formData.append('enable_mfa', settings.enableMFA ? 1 : 0);
      formData.append('enable_sso', settings.enableSSO ? 1 : 0);
      formData.append('enable_audit_log', settings.enableAuditLog ? 1 : 0);
      formData.append('maintenance_mode', settings.maintenanceMode ? 1 : 0);
      formData.append('email_notifications_enabled', settings.emailNotificationsEnabled ? 1 : 0);
      
      // Append files if they are new File objects
      if (settings.companyLogo instanceof File) {
        formData.append('company_logo', settings.companyLogo);
      }
      
      if (settings.favicon instanceof File) {
        formData.append('favicon', settings.favicon);
      }
      
      const response = await axios.post('/general-settings', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      setIsModified(false);

      const updatedTimeout = settings.sessionTimeout ? String(settings.sessionTimeout) : "30";
      sessionStorage.setItem('session_timeout', updatedTimeout);
      sessionStorage.setItem('last_activity_time', String(Date.now()));
      window.dispatchEvent(new CustomEvent('session-timeout-updated', { detail: updatedTimeout }));

      // Refresh settings after save
      await fetchSettings();
      
      toast.success(`General settings updated successfully! Session timeout set to ${updatedTimeout} minutes.`);
      
    } catch (error) {
      console.error(error);
      toast.error(error?.response?.data?.message || 'Failed to update settings');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    // Reset to current saved settings by refetching
    fetchSettings();
    setIsModified(false);
    if (logoInputRef.current) logoInputRef.current.value = '';
    if (faviconInputRef.current) faviconInputRef.current.value = '';
  };

  return (
    <div className="space-y-8">
      {/* Organization Settings */}
      <div className="bg-card rounded-lg border border-border p-6">
        <div className="flex items-center space-x-2 mb-6">
          <Icon name="Building" size={20} className="text-primary" />
          <h3 className="text-lg font-semibold text-card-foreground">Organization Settings</h3>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Input
            label="Organization Name"
            type="text"
            value={settings?.organizationName}
            onChange={(e) => handleInputChange('organizationName', e?.target?.value)}
            placeholder="Enter organization name"
            required
          />
          
          <Select
            label="Default Timezone"
            options={timezoneOptions}
            value={settings?.timezone}
            onChange={(value) => handleInputChange('timezone', value)}
          />
          
          <Select
            label="Date Format"
            options={dateFormatOptions}
            value={settings?.dateFormat}
            onChange={(value) => handleInputChange('dateFormat', value)}
          />
          
          <Select
            label="Default Currency"
            options={currencyOptions}
            value={settings?.currency}
            onChange={(value) => handleInputChange('currency', value)}
          />
          
          <Select
            label="System Language"
            options={languageOptions}
            value={settings?.language}
            onChange={(value) => handleInputChange('language', value)}
          />
          
          <Select
            label="Session Timeout"
            description="Automatic logout after inactivity"
            options={sessionTimeoutOptions}
            value={settings?.sessionTimeout}
            onChange={(value) => handleInputChange('sessionTimeout', value)}
          />

          {/* Company Logo Upload */}
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-card-foreground mb-2">
              Company Logo
            </label>
            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0">
                {logoPreview ? (
                  <div className="relative">
                    <img 
                      src={logoPreview} 
                      alt="Company Logo Preview" 
                      className="h-24 w-24 object-contain rounded-lg border border-border bg-muted p-2"
                    />
                    <button
                      type="button"
                      onClick={() => handleRemoveImage('logo')}
                      className="absolute -top-2 -right-2 p-1 bg-error rounded-full text-white hover:bg-error/80 transition-colors"
                    >
                      <Icon name="X" size={12} />
                    </button>
                  </div>
                ) : (
                  <div className="h-24 w-24 rounded-lg border-2 border-dashed border-border bg-muted flex items-center justify-center">
                    <Icon name="Image" size={32} className="text-muted-foreground" />
                  </div>
                )}
              </div>
              <div className="flex-1 space-y-2">
                <input
                  ref={logoInputRef}
                  type="file"
                  accept="image/jpeg,image/jpg,image/png,image/svg+xml,image/webp"
                  onChange={(e) => handleFileUpload('companyLogo', e.target.files[0], 'logo')}
                  className="hidden"
                  id="logo-upload"
                />
                <div className="flex items-center space-x-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => logoInputRef.current?.click()}
                  >
                    <Icon name="Upload" size={16} className="mr-2" />
                    Upload Logo
                  </Button>
                  {logoPreview && (
                    <Button
                      type="button"
                      variant="ghost"
                      onClick={() => handleRemoveImage('logo')}
                    >
                      Remove
                    </Button>
                  )}
                </div>
                <p className="text-xs text-muted-foreground">
                  Recommended size: 200x200px. Max size: 5MB. Supported formats: JPEG, PNG, SVG, WEBP
                </p>
              </div>
            </div>
          </div>

          {/* Favicon Upload */}
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-card-foreground mb-2">
              Favicon
            </label>
            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0">
                {faviconPreview ? (
                  <div className="relative">
                    <img 
                      src={faviconPreview} 
                      alt="Favicon Preview" 
                      className="h-16 w-16 object-contain rounded-lg border border-border bg-muted p-2"
                    />
                    <button
                      type="button"
                      onClick={() => handleRemoveImage('favicon')}
                      className="absolute -top-2 -right-2 p-1 bg-error rounded-full text-white hover:bg-error/80 transition-colors"
                    >
                      <Icon name="X" size={12} />
                    </button>
                  </div>
                ) : (
                  <div className="h-16 w-16 rounded-lg border-2 border-dashed border-border bg-muted flex items-center justify-center">
                    <Icon name="Globe" size={24} className="text-muted-foreground" />
                  </div>
                )}
              </div>
              <div className="flex-1 space-y-2">
                <input
                  ref={faviconInputRef}
                  type="file"
                  accept="image/jpeg,image/jpg,image/png,image/svg+xml,image/webp"
                  onChange={(e) => handleFileUpload('favicon', e.target.files[0], 'favicon')}
                  className="hidden"
                  id="favicon-upload"
                />
                <div className="flex items-center space-x-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => faviconInputRef.current?.click()}
                  >
                    <Icon name="Upload" size={16} className="mr-2" />
                    Upload Favicon
                  </Button>
                  {faviconPreview && (
                    <Button
                      type="button"
                      variant="ghost"
                      onClick={() => handleRemoveImage('favicon')}
                    >
                      Remove
                    </Button>
                  )}
                </div>
                <p className="text-xs text-muted-foreground">
                  Recommended size: 32x32px or 16x16px. Max size: 2MB. Supported formats: JPEG, PNG, SVG, WEBP
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Security Settings */}
      <div className="bg-card rounded-lg border border-border p-6">
        <div className="flex items-center space-x-2 mb-6">
          <Icon name="Shield" size={20} className="text-primary" />
          <h3 className="text-lg font-semibold text-card-foreground">Security Settings</h3>
        </div>
        
        <div className="space-y-4">
          <Checkbox
            label="Enable Multi-Factor Authentication (MFA)"
            description="Require additional verification for user logins"
            checked={settings?.enableMFA}
            onChange={(e) => handleInputChange('enableMFA', e?.target?.checked)}
          />
          
          <Checkbox
            label="Enable Single Sign-On (SSO)"
            description="Allow users to authenticate via Active Directory/LDAP"
            checked={settings?.enableSSO}
            onChange={(e) => handleInputChange('enableSSO', e?.target?.checked)}
          />
          
          <Checkbox
            label="Enable Audit Logging"
            description="Track all user actions and system changes"
            checked={settings?.enableAuditLog}
            onChange={(e) => handleInputChange('enableAuditLog', e?.target?.checked)}
          />
        </div>
      </div>

      {/* Email Notifications Control */}
      <div className="bg-card rounded-lg border border-border p-6">
        <div className="flex items-center space-x-2 mb-6">
          <Icon name="Mail" size={20} className="text-primary" />
          <h3 className="text-lg font-semibold text-card-foreground">Email Notifications Control</h3>
        </div>
        
        <div className="space-y-4">
          <div className="flex items-center justify-between p-4 bg-muted/40 border border-border rounded-lg">
            <div className="flex items-center space-x-3">
              <div className={`p-2 rounded-lg ${settings.emailNotificationsEnabled ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground'}`}>
                <Icon name="Bell" size={20} />
              </div>
              <div>
                <p className="font-medium text-card-foreground">System-wide Email Notifications</p>
                <p className="text-sm text-muted-foreground">
                  Master toggle to enable or disable all automated email alerts, audit updates, warranty notices, and user lifecycle emails
                </p>
              </div>
            </div>
            <Checkbox
              checked={settings?.emailNotificationsEnabled}
              onChange={(e) => handleInputChange('emailNotificationsEnabled', e?.target?.checked)}
            />
          </div>

          {!settings?.emailNotificationsEnabled && (
            <div className="p-3.5 bg-amber-500/10 border border-amber-500/20 rounded-lg flex items-center gap-2 text-sm text-amber-700 dark:text-amber-400">
              <Icon name="AlertTriangle" size={16} />
              <span>Email notifications are currently disabled system-wide. No outgoing email alerts will be dispatched.</span>
            </div>
          )}
        </div>
      </div>

      {/* System Maintenance */}
      <div className="bg-card rounded-lg border border-border p-6">
        <div className="flex items-center space-x-2 mb-6">
          <Icon name="Settings" size={20} className="text-primary" />
          <h3 className="text-lg font-semibold text-card-foreground">System Maintenance</h3>
        </div>
        
        <div className="space-y-4">
          <div className="flex items-center justify-between p-4 bg-warning/10 border border-warning/20 rounded-lg">
            <div className="flex items-center space-x-3">
              <Icon name="AlertTriangle" size={20} className="text-warning" />
              <div>
                <p className="font-medium text-card-foreground">Maintenance Mode</p>
                <p className="text-sm text-muted-foreground">Temporarily disable user access for system updates</p>
              </div>
            </div>
            <Checkbox
              checked={settings?.maintenanceMode}
              onChange={(e) => handleInputChange('maintenanceMode', e?.target?.checked)}
            />
          </div>
          
          {settings?.maintenanceMode && (
            <div className="p-4 bg-error/10 border border-error/20 rounded-lg">
              <div className="flex items-center space-x-2 mb-2">
                <Icon name="AlertCircle" size={16} className="text-error" />
                <p className="font-medium text-error">Warning: Maintenance Mode Active</p>
              </div>
              <p className="text-sm text-muted-foreground">
                Users will be unable to access the system. Only administrators can log in during maintenance mode.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Appearance & Theme Section */}
      <div className="bg-card rounded-lg border border-border p-6">
        <div className="flex items-center space-x-2 mb-2">
          <Icon name="Palette" size={20} className="text-primary" />
          <h3 className="text-lg font-semibold text-card-foreground">Appearance & Theme Mode</h3>
        </div>
        <p className="text-sm text-muted-foreground mb-4">
          Select your visual appearance preference across the system. Choose between crisp Light mode, eye-friendly Dark mode, or automatic System mode.
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {[
            { id: 'light', label: 'Light Mode', icon: 'Sun', desc: 'Crisp, high-contrast light theme' },
            { id: 'dark', label: 'Dark Mode', icon: 'Moon', desc: 'Sleek dark theme for low-light environments' },
            { id: 'system', label: 'System Mode', icon: 'Monitor', desc: 'Automatically matches your device OS theme' },
          ].map((item) => {
            const isSelected = (theme || 'system') === item.id;
            return (
              <button
                key={item.id}
                type="button"
                onClick={() => {
                  setTheme(item.id);
                  toast.success(`Appearance set to ${item.label}`);
                }}
                className={`flex flex-col items-start p-3.5 rounded-xl border text-left transition-all ${
                  isSelected
                    ? 'border-blue-500 bg-blue-50/70 dark:bg-blue-950/40 ring-2 ring-blue-500/20 text-blue-950 dark:text-blue-100'
                    : 'border-border hover:border-blue-300 dark:hover:border-blue-700 bg-card text-card-foreground'
                }`}
              >
                <div className="flex items-center justify-between w-full mb-1.5">
                  <div className="flex items-center gap-2">
                    <span className={`p-1.5 rounded-lg ${isSelected ? 'bg-blue-600 text-white' : 'bg-muted text-muted-foreground'}`}>
                      <Icon name={item.icon} size={16} />
                    </span>
                    <span className="font-semibold text-sm">{item.label}</span>
                  </div>
                  {isSelected && (
                    <Icon name="CheckCircle2" size={16} className="text-blue-600 dark:text-blue-400" />
                  )}
                </div>
                <p className="text-xs text-muted-foreground">{item.desc}</p>
              </button>
            );
          })}
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex items-center justify-between pt-6 border-t border-border">
        <div className="flex items-center space-x-2 text-sm text-muted-foreground">
          <Icon name="Clock" size={16} />
          <span>
            Last updated: {new Date().toLocaleString()}
          </span>
        </div>
        
        <div className="flex items-center space-x-3">
          <Button
            variant="outline"
            onClick={handleReset}
            disabled={!isModified || loading}
          >
            Reset Changes
          </Button>
          <Button
            variant="default"
            onClick={handleSave}
            disabled={!isModified || loading}
            iconName={loading ? "Loader2" : "Save"}
            iconPosition="left"
          >
            {loading ? "Saving..." : "Save Settings"}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default GeneralSettingsTab;